/**
 * Floating panel for the full-screen explorer, with two tabs:
 * - Networks: pick a network and see where it lives (rooms, devices, recorded
 *   presence) and how it is joined up. Confirmed rooms come first; devices whose
 *   service is unconfirmed are listed apart so they never read as a confirmed
 *   location.
 * - Racks: every rack's devices, top to bottom, grouped by room, filterable by
 *   text or by the selected network.
 * It minimises to a pill so the scene can use the full width.
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '@pazportal/ui';
import { kindColor } from '../../lib/deviceColors';
import {
  CONFIDENCE_LABEL,
  MEDIUM_LABEL,
  networkColor,
  networkFootprint,
  rackInventory,
  rackTitle,
  ruLabel,
  type RackPlant,
} from '../../lib/rackModel';

type PanelTab = 'networks' | 'racks';

export function NetworkPanel({
  plant, networkId, selectedRackId, selectedUnitId, tab, onTab, onMinimize,
  onSelectNetwork, onSelectUnit, onSelectRoom, onSelectRack,
}: {
  plant: RackPlant;
  networkId: string | null;
  selectedRackId: string | null;
  selectedUnitId: string | null;
  tab: PanelTab;
  onTab: (tab: PanelTab) => void;
  onMinimize: () => void;
  onSelectNetwork: (id: string | null) => void;
  onSelectUnit: (id: string) => void;
  onSelectRoom: (id: string) => void;
  onSelectRack: (id: string) => void;
}) {
  const fp = networkId ? networkFootprint(plant, networkId) : null;
  const unit = selectedUnitId ? plant.unitById.get(selectedUnitId) : undefined;
  const deviceCount = plant.units.filter((u) => u.kind !== 'vacant').length;

  return (
    <aside
      className="pointer-events-auto flex max-h-full w-80 flex-col overflow-hidden rounded-lg border border-border bg-bg-elevated/95 shadow-2xl backdrop-blur"
      aria-label="Networks and racks"
    >
      <header className="flex items-center gap-2 border-b border-divider px-3 py-2">
        <div role="tablist" aria-label="Panel" className="flex flex-1 overflow-hidden rounded-sm border border-border">
          {(['networks', 'racks'] as const).map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => onTab(t)}
              className={cn('h-7 flex-1 px-2 text-xs font-semibold', tab === t ? 'bg-primary text-text-inverse' : 'text-text hover:bg-surface-3')}
            >
              {t === 'networks' ? `Networks (${plant.networks.length})` : `Racks (${plant.racks.length})`}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onMinimize}
          aria-label="Minimise panel"
          title="Minimise"
          className="grid h-7 w-7 flex-none place-items-center rounded-sm border border-border text-text hover:bg-surface-3"
        >
          <MinimiseIcon />
        </button>
      </header>

      {tab === 'racks' ? (
        <RacksTab
          plant={plant}
          networkId={networkId}
          selectedRackId={selectedRackId}
          selectedUnitId={selectedUnitId}
          deviceCount={deviceCount}
          onSelectRack={onSelectRack}
          onSelectUnit={onSelectUnit}
          onSelectNetwork={onSelectNetwork}
        />
      ) : (
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex items-center justify-between px-4 pb-1 pt-2.5">
          <p className="text-[11px] text-text-muted">Pick one to see where it lives and how it connects.</p>
          {networkId && (
            <button className="text-xs text-primary hover:underline" onClick={() => onSelectNetwork(null)}>All</button>
          )}
        </div>
        {plant.networks.length === 0 ? (
          <p className="p-4 text-sm text-text-muted">No networks recorded for this site.</p>
        ) : (
          <ul className="divide-y divide-divider">
            {plant.networks.map((n) => {
              const f = networkFootprint(plant, n.id)!;
              const codes = f.rooms.filter((r) => f.roomIds.has(r.roomId)).map((r) => plant.roomById.get(r.roomId)!.code);
              const active = n.id === networkId;
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => onSelectNetwork(active ? null : n.id)}
                    aria-pressed={active}
                    className={cn('flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-surface-3', active && 'bg-surface-3')}
                  >
                    <span className="mt-1 h-3 w-3 flex-none rounded-full" style={{ background: networkColor(n) }} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-text">{n.name}</span>
                        <span className="flex-none font-mono text-[10.5px] text-text-muted">{f.unitIds.size} dev</span>
                      </span>
                      {n.owner && <span className="block truncate text-[11px] text-text-muted">{n.owner}</span>}
                      <span className="mt-1 flex flex-wrap gap-1">
                        {codes.map((c) => (
                          <span key={c} className="rounded-sm border border-border px-1.5 py-px font-mono text-[10px] text-text">{c}</span>
                        ))}
                      </span>
                    </span>
                  </button>

                  {active && fp && (
                    <div className="space-y-3 bg-surface-2 px-4 pb-4 pt-2 text-xs">
                      {n.description && <p className="text-text-muted">{n.description}</p>}
                      {fp.rooms.map((r) => {
                        const room = plant.roomById.get(r.roomId)!;
                        const confirmed = fp.roomIds.has(r.roomId);
                        return (
                          <div key={r.roomId}>
                            <button
                              className="mb-1 flex w-full items-center justify-between text-left"
                              onClick={() => onSelectRoom(r.roomId)}
                            >
                              <span className="font-semibold text-text">{room.code}{room.name ? ` · ${room.name}` : ''}</span>
                              <span className={cn('text-[10.5px]', confirmed ? 'text-primary' : 'text-warning')}>{confirmed ? 'fly to →' : 'unconfirmed'}</span>
                            </button>
                            <ul className="space-y-0.5">
                              {r.units.map((u) => (
                                <li key={u.id}>
                                  <UnitRow label={u.label} sub={`${plant.rackById.get(u.rack_id)?.code} · ${ruLabel(u)}`} selected={u.id === selectedUnitId} onClick={() => onSelectUnit(u.id)} />
                                </li>
                              ))}
                              {r.presenceOnly && (
                                <li className="rounded-sm border border-dashed px-2 py-1.5 text-text-muted" style={{ borderColor: networkColor(n) }}>
                                  <span className="font-medium text-text">Present, no device recorded</span>
                                  {r.presenceOnly.note && <span className="block">{r.presenceOnly.note}</span>}
                                </li>
                              )}
                              {r.unconfirmed.map((u) => (
                                <li key={u.id}>
                                  <UnitRow label={u.label} sub={`${ruLabel(u)} · not confirmed in service`} selected={u.id === selectedUnitId} onClick={() => onSelectUnit(u.id)} muted />
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}

                      <div>
                        <h3 className="mb-1 text-[10.5px] font-medium uppercase tracking-wider text-text-muted">Connections ({fp.links.length})</h3>
                        {fp.links.length === 0 ? (
                          <p className="text-text-muted">No connection recorded for this network yet.</p>
                        ) : (
                          <ul className="space-y-1">
                            {fp.links.map((l) => {
                              const from = plant.unitById.get(l.from_unit_id)!;
                              const to = l.to_unit_id ? plant.unitById.get(l.to_unit_id) : undefined;
                              const toRack = to?.rack_id ?? l.to_rack_id!;
                              return (
                                <li key={l.id} className="rounded-sm border border-border bg-bg-elevated px-2 py-1.5">
                                  <div className="font-medium text-text">{l.label ?? MEDIUM_LABEL[l.medium]}</div>
                                  <div className="text-text-muted">
                                    {from.label} → {to ? to.label : rackTitle(plant, toRack)}
                                  </div>
                                  <div className="mt-1 flex items-center justify-between">
                                    <span className="text-[10.5px] text-text-muted">{MEDIUM_LABEL[l.medium]} · {CONFIDENCE_LABEL[l.confidence]}</span>
                                    <span className="flex gap-2">
                                      <button className="text-primary hover:underline" onClick={() => onSelectUnit(from.id)}>{plant.rackById.get(from.rack_id)?.code}</button>
                                      <button className="text-primary hover:underline" onClick={() => (to ? onSelectUnit(to.id) : onSelectRack(toRack))}>
                                        {plant.rackById.get(toRack)?.code} →
                                      </button>
                                    </span>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                      {n.source && <p className="text-[10.5px] text-text-faint">Source: {n.source}</p>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      )}

      {unit && (
        <footer className="border-t border-divider px-4 py-3 text-xs">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate font-semibold text-text">{unit.label}</span>
            <span className="flex-none font-mono text-text-muted">{plant.rackById.get(unit.rack_id)?.code} · {ruLabel(unit)}</span>
          </div>
          {unit.detail && <p className="text-text-muted">{unit.detail}</p>}
          <div className="mt-1.5 flex flex-wrap gap-1">
            {(plant.networksByUnit.get(unit.id) ?? []).map((m) => {
              const n = plant.networkById.get(m.network_id)!;
              return (
                <button
                  key={m.id}
                  onClick={() => onSelectNetwork(n.id)}
                  className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10.5px] text-text hover:border-primary"
                >
                  <span className="h-2 w-2 rounded-full" style={{ background: networkColor(n) }} />
                  {n.name}{m.confidence === 'unknown' ? ' (unconfirmed)' : ''}
                </button>
              );
            })}
          </div>
          {(plant.linksByUnit.get(unit.id) ?? []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(plant.linksByUnit.get(unit.id) ?? []).map((end) => (
                <button
                  key={end.link.id}
                  onClick={() => (end.otherUnitId ? onSelectUnit(end.otherUnitId) : onSelectRack(end.otherRackId))}
                  className="rounded-sm bg-primary px-2 py-1 text-[11px] font-medium text-text-inverse hover:bg-primary-hover"
                >
                  {end.link.label ?? MEDIUM_LABEL[end.link.medium]} → {plant.rackById.get(end.otherRackId)?.code}
                </button>
              ))}
            </div>
          )}
        </footer>
      )}
    </aside>
  );
}

function UnitRow({ label, sub, selected, onClick, muted }: { label: string; sub: string; selected: boolean; onClick: () => void; muted?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-baseline justify-between gap-2 rounded-sm px-2 py-1 text-left hover:bg-surface-3',
        selected && 'bg-primary-soft',
      )}
    >
      <span className={cn('truncate', muted ? 'text-text-muted' : 'text-text')}>{label}</span>
      <span className="flex-none font-mono text-[10px] text-text-muted">{sub}</span>
    </button>
  );
}

/** The minimised panel: one pill that restores it. */
export function NetworkPanelPill({ plant, networkId, onRestore }: { plant: RackPlant; networkId: string | null; onRestore: () => void }) {
  const network = networkId ? plant.networkById.get(networkId) : undefined;
  return (
    <button
      type="button"
      onClick={onRestore}
      aria-label="Show networks and racks panel"
      className="pointer-events-auto flex h-9 items-center gap-2 self-start rounded-full border border-border bg-bg-elevated/95 px-3 text-xs font-semibold text-text shadow-xl backdrop-blur hover:border-primary"
    >
      {network && <span className="h-2.5 w-2.5 rounded-full" style={{ background: networkColor(network) }} />}
      <span>Networks · Racks</span>
      <span className="font-mono text-[10.5px] font-normal text-text-muted">{plant.networks.length} · {plant.racks.length}</span>
      <span aria-hidden className="text-text-muted">‹</span>
    </button>
  );
}

function RacksTab({
  plant, networkId, selectedRackId, selectedUnitId, deviceCount, onSelectRack, onSelectUnit, onSelectNetwork,
}: {
  plant: RackPlant;
  networkId: string | null;
  selectedRackId: string | null;
  selectedUnitId: string | null;
  deviceCount: number;
  onSelectRack: (id: string) => void;
  onSelectUnit: (id: string) => void;
  onSelectNetwork: (id: string | null) => void;
}) {
  const [text, setText] = useState('');
  const [onlyNetwork, setOnlyNetwork] = useState(false);
  const [open, setOpen] = useState<Set<string>>(() => new Set(selectedRackId ? [selectedRackId] : []));
  const selectedRef = useRef<HTMLButtonElement | null>(null);
  const network = networkId ? plant.networkById.get(networkId) : undefined;
  const filterNetwork = onlyNetwork && network ? network.id : null;
  const filtering = text.trim().length > 0 || !!filterNetwork;
  const rooms = rackInventory(plant, { text, networkId: filterNetwork });
  const shown = rooms.reduce((n, r) => n + r.racks.reduce((m, k) => m + k.units.length, 0), 0);

  // Follow the selection: the selected rack opens, the selected device scrolls into view.
  useEffect(() => {
    if (selectedRackId) setOpen((prev) => (prev.has(selectedRackId) ? prev : new Set(prev).add(selectedRackId)));
  }, [selectedRackId]);
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedUnitId]);

  const toggle = (id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-2 border-b border-divider px-3 py-2">
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Filter devices — label, IP, owner"
          aria-label="Filter devices"
          className="h-8 w-full rounded-sm border border-border bg-surface px-2 text-xs text-text placeholder:text-text-faint"
        />
        <div className="flex items-center justify-between gap-2 text-[11px] text-text-muted">
          <span>{filtering ? `${shown} of ${deviceCount} devices` : `${deviceCount} devices in ${plant.racks.length} racks`}</span>
          {network ? (
            <label className="flex items-center gap-1.5 text-text">
              <input type="checkbox" checked={onlyNetwork} onChange={(e) => setOnlyNetwork(e.target.checked)} />
              <span className="h-2 w-2 rounded-full" style={{ background: networkColor(network) }} />
              <span className="max-w-[9rem] truncate">Only {network.name}</span>
            </label>
          ) : (
            <span className="text-text-faint">Pick a network to filter by it</span>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {rooms.length === 0 ? (
          <p className="p-4 text-sm text-text-muted">{filtering ? 'No device matches.' : 'No racks recorded for this site.'}</p>
        ) : (
          rooms.map(({ room, racks }) => (
            <section key={room.id} aria-label={room.code}>
              <h3 className="sticky top-0 z-[1] border-b border-divider bg-surface-2 px-3 py-1 text-[10.5px] font-semibold uppercase tracking-wider text-text-muted">
                {room.code}{room.name ? ` · ${room.name}` : ''}
              </h3>
              <ul>
                {racks.map(({ rack, units, total }) => {
                  const isOpen = filtering || open.has(rack.id);
                  const current = rack.id === selectedRackId;
                  return (
                    <li key={rack.id} className="border-b border-divider">
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => {
                          onSelectRack(rack.id);
                          if (!filtering) toggle(rack.id);
                        }}
                        className={cn('flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-surface-3', current && 'bg-surface-3')}
                      >
                        <span aria-hidden className={cn('text-[10px] text-text-muted transition-transform', isOpen && 'rotate-90')}>▶</span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-semibold text-text">{rackTitle(plant, rack.id)}</span>
                          <span className="block font-mono text-[10px] text-text-muted">
                            {rack.height_u}U · {filtering ? `${units.length} of ${total}` : total} device{total === 1 ? '' : 's'}
                          </span>
                        </span>
                      </button>
                      {isOpen && (
                        <ul className="pb-1.5">
                          {units.length === 0 && <li className="px-3 py-1 text-[11px] text-text-muted">No positions recorded.</li>}
                          {units.map((u) => {
                            const selected = u.id === selectedUnitId;
                            const memberships = plant.networksByUnit.get(u.id) ?? [];
                            return (
                              <li key={u.id}>
                                <button
                                  ref={selected ? selectedRef : undefined}
                                  type="button"
                                  onClick={() => onSelectUnit(u.id)}
                                  aria-current={selected || undefined}
                                  className={cn(
                                    'flex w-full items-center gap-2 py-1 pl-7 pr-3 text-left text-xs hover:bg-surface-3',
                                    selected && 'bg-primary-soft',
                                  )}
                                >
                                  <span
                                    aria-hidden
                                    className={cn('h-3 w-1.5 flex-none rounded-[1px]', u.confidence === 'deduced' && 'opacity-60')}
                                    style={{ background: kindColor(u.kind) }}
                                  />
                                  <span className={cn('min-w-0 flex-1 truncate', u.confidence === 'unknown' ? 'text-text-muted' : 'text-text')}>
                                    {u.label}
                                  </span>
                                  {memberships.map((m) => {
                                    const n = plant.networkById.get(m.network_id)!;
                                    return (
                                      <span
                                        key={m.id}
                                        role="button"
                                        tabIndex={-1}
                                        title={`${n.name}${m.confidence === 'unknown' ? ' (unconfirmed)' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onSelectNetwork(n.id);
                                        }}
                                        className={cn('h-2 w-2 flex-none rounded-full', m.confidence === 'unknown' && 'opacity-40')}
                                        style={{ background: networkColor(n) }}
                                      />
                                    );
                                  })}
                                  {u.confidence !== 'verified' && (
                                    <span
                                      className={cn(
                                        'flex-none text-[9.5px] uppercase tracking-wide',
                                        u.confidence === 'contested' || u.confidence === 'unknown' ? 'text-warning' : 'text-text-faint',
                                      )}
                                    >
                                      {CONFIDENCE_LABEL[u.confidence]}
                                    </span>
                                  )}
                                  <span className="w-14 flex-none text-right font-mono text-[10px] text-text-muted">{ruLabel(u)}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

function MinimiseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
      <path d="M2 9h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
